
import React, { useState } from 'react';
import { X, Scale, MapPin, Clock, ShieldAlert, CheckCircle2 } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';

interface LegalGuideProps {
  onClose: () => void;
  initialCountry?: string | null;
}

const iconMap: Record<string, any> = {
  MapPin: MapPin,
  Clock: Clock,
  ShieldAlert: ShieldAlert,
  CheckCircle2: CheckCircle2
};

const colorMap: Record<string, string> = {
  amber: 'text-amber-600 bg-amber-500/10 border-amber-500/20',
  blue: 'text-blue-600 bg-blue-500/10 border-blue-500/20',
  red: 'text-red-600 bg-red-500/10 border-red-500/20',
  emerald: 'text-emerald-600 bg-emerald-500/10 border-emerald-500/20'
};

const LegalGuide: React.FC<LegalGuideProps> = ({ onClose, initialCountry }) => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState(initialCountry?.toLowerCase() || 'no');

  // We cast to any because i18next types for nested objects can be tricky
  const countries = t('legal.countries', { returnObjects: true }) as any;
  const tabs = t('legal.tabs', { returnObjects: true }) as any;
  const activeCountry = countries[activeTab];

  return (
    <div className="fixed left-0 right-0 bottom-0 z-[10001] flex items-center justify-center px-4 pb-4" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
      <div className="absolute inset-0 bg-stone-950/90 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-modal shadow-3xl overflow-hidden animate-in zoom-in-95 fade-in duration-300 flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="p-8 pb-4 shrink-0 flex justify-between items-start bg-[var(--bg-surface)]/50">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-emerald-500/10 rounded-2xl border border-emerald-500/20 text-emerald-500">
              <Scale size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-black text-[var(--text-primary)] tracking-tighter">{t('legal.title')}</h2>
              <p className="text-2xs text-[var(--text-muted)] font-bold uppercase tracking-widest">{t('legal.subtitle')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors bg-[var(--bg-input)] rounded-xl">
            <X size={20} />
          </button>
        </div>

        {/* Tab Switcher */}
        <div className="px-8 pb-4 shrink-0">
          <div className="flex gap-1 p-1 bg-[var(--bg-input)] rounded-2xl overflow-x-auto no-scrollbar">
            {Object.entries(tabs).map(([id, label]: [string, any]) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all whitespace-nowrap ${
                  activeTab === id 
                    ? 'bg-[var(--bg-surface)] text-[var(--text-primary)] shadow-sm' 
                    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 pt-4 space-y-6 custom-scrollbar">
          <div className="grid grid-cols-1 gap-4">
            {activeCountry.rules.map((rule: any, idx: number) => {
              const Icon = iconMap[rule.icon] || MapPin;
              const colorClasses = colorMap[rule.color] || colorMap.amber;
              
              return (
                <div key={idx} className="p-6 bg-white rounded-panel flex flex-col md:flex-row gap-6 items-center shadow-xl animate-in fade-in slide-in-from-bottom-2 duration-500" style={{ animationDelay: `${idx * 100}ms` }}>
                  <div className="flex-1 space-y-3">
                    <div className={`flex items-center gap-2 font-black text-2xs uppercase tracking-widest ${colorClasses.split(' ')[0]}`}>
                      <Icon size={14} /> {rule.title}
                    </div>
                    <p className="text-stone-800 text-xs leading-relaxed font-semibold">
                      <Trans 
                        i18nKey={`legal.countries.${activeTab}.rules.${idx}.desc`}
                        components={{ black: <strong className="text-black font-black" /> }} 
                      />
                    </p>
                  </div>
                  {rule.image && (
                    <div className="relative group overflow-hidden rounded-2xl shadow-inner bg-white flex flex-col items-center justify-center py-2 px-1">
                      <img 
                        src={rule.image} 
                        className="w-full md:w-44 h-24 object-contain transition-transform duration-500 group-hover:scale-105" 
                        alt={rule.title} 
                      />
                      <div className="mt-1 text-4xs font-black uppercase tracking-tighter text-[#2C4239] text-center opacity-80 leading-none">
                        {rule.title}
                      </div>
                      <div className="absolute inset-0 ring-1 ring-inset ring-black/5 rounded-2xl pointer-events-none" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[var(--border-secondary)] shrink-0 bg-[var(--bg-surface)]/80">
          <button onClick={onClose} className="w-full bg-[var(--text-primary)] text-[var(--bg-surface)] py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg active:scale-[0.98] transition-transform">
            {t('legal.button_understood')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LegalGuide;
